import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type ThemePreference,
  type ResolvedTheme,
  readPersistedPreference,
  persistPreference,
  systemPrefersDark,
  resolveTheme,
  applyResolvedTheme,
  subscribeToPreferenceChanges,
  subscribeToSystemChanges,
} from "./storage";

interface ThemeContextType {
  /** What the user chose: "light", "dark", or "system" (follow the OS). */
  preference: ThemePreference;
  /** What is actually rendered right now — "system" resolved against the OS. */
  resolvedTheme: ResolvedTheme;
  /** Set (and persist) the preference. */
  setTheme: (preference: ThemePreference) => void;
}

// A light-theme default (like ImpersonationContext) rather than a throwing null:
// leaf components that read the theme — every chart — are unit-tested rendered
// bare, and should degrade to the default theme instead of exploding. In the
// real app the provider at the root always supplies the live value.
const ThemeContext = createContext<ThemeContextType>({
  preference: "system",
  resolvedTheme: "light",
  setTheme: () => {},
});

/** Read/adjust the active theme. */
export function useTheme(): ThemeContextType {
  return useContext(ThemeContext);
}

/**
 * Owns the theme preference and keeps the document in sync with it.
 *
 * Mounted above the whole app (including the toast layer and the Amplify login),
 * so every surface can read `resolvedTheme`. The pre-paint script in index.html
 * has already set the correct `.dark` class before first paint; this provider
 * takes over from there and re-applies it on every change.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(() =>
    readPersistedPreference()
  );
  const [prefersDark, setPrefersDark] = useState<boolean>(() =>
    systemPrefersDark()
  );

  const resolvedTheme = resolveTheme(preference, prefersDark);

  // Reflect the resolved theme onto <html> whenever it changes.
  useEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Track the OS setting so a "System" choice updates live.
  useEffect(
    () => subscribeToSystemChanges(() => setPrefersDark(systemPrefersDark())),
    []
  );

  // Mirror a change made in another tab.
  useEffect(() => subscribeToPreferenceChanges(setPreference), []);

  const setTheme = useCallback((next: ThemePreference) => {
    setPreference(next);
    persistPreference(next);
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({ preference, resolvedTheme, setTheme }),
    [preference, resolvedTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
